import React, { useState, useEffect } from 'react';
import { Hub } from '@aws-amplify/core';
import Button from '@amzn/awsui-components-react/polaris/button';
import ColumnLayout from '@amzn/awsui-components-react/polaris/column-layout';
import FormSection from '@amzn/awsui-components-react/polaris/form-section';

import Auth from '@aws-amplify/auth';
import './App.css';

import Footer from './components/Footer';
import Header from './components/Header';
import Main from './components/Main';
import Container from './components/Container';
import AttributeRow from './components/AttributeRow';
import config from './services/config';

const getUserAttr = (user: any, attr: string) =>
  user?.signInUserSession?.idToken?.payload?.[attr];

export const isValidName = (name: string) =>
  /^\p{L}[\p{L}\p{M}'+-]*(?:,? [\p{L}\p{M}'+-]+)+$/u.test(name?.trim());

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email?.trim());

const isValidNameID = (nameId: string, email: string): boolean =>
  !!(nameId && email && nameId.trim() !== email.trim());

// To make it easier to see all usage flows all logic has been put into one component.
const App = () => {
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showError, setShowError] = useState(false);

  // Setup Amplify Hub listener to receive auth events.
  useEffect(() => {
    getUser().then((userData) => setUser(userData));
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
        case 'cognitoHostedUI':
          getUser().then((userData) => setUser(userData));
          break;
        case 'signOut':
          setUser(null);
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          console.error('Sign in failure', data);
          setShowError(true);
          break;
      }
    });
  }, []);

  const getUser = () =>
    Auth.currentAuthenticatedUser().catch(() => {
      console.log('Not signed in');
      setShowLogin(true);
    });

  const handleSignIn = () => Auth.federatedSignIn();
  const handleSignOut = async () => {
    try {
      let clientId = getUserAttr(user, 'aud');
      const amplifyConfig = Auth.configure();
      if (!clientId) {
        clientId = amplifyConfig.userPoolWebClientId;
      }
      // Reads from ConfigOauthDomain of GandalfIDPTesterLPT: lib/amazon/lpt/gandalfidptester_lpt.rb.
      const gandalfDomain = amplifyConfig.oauth?.domain;
      const appConfig = config.get();
      const signInDomain = appConfig.Gandalf.customSignInDomain;
      const logoutUri = encodeURIComponent(
        `https://${signInDomain}/logout?client_id=${clientId}`
      );
      const cognitoLogoutUrl = `https://${gandalfDomain}/logout?client_id=${clientId}&response_type=code&logout_uri=${logoutUri}`;
      setTimeout(() => {
        window.location.href = cognitoLogoutUrl;
      }, 0);

      await Auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  var currentTime = new Date().toUTCString();

  var body;
  var head;
  if (showError) {
    head = <h2>SSO authentication failed</h2>;
    body = (
      <ColumnLayout>
        <div data-awsui-column-layout-root="true">
          <p> An error occurred, please try again later. </p>
          <p> Error Time: {currentTime} </p>
        </div>
      </ColumnLayout>
    );
  } else if (!user && showLogin) {
    head = <h2>SSO authentication handshake</h2>;
    body = (
      <ColumnLayout>
        <div data-awsui-column-layout-root="true">
          <p>Please login to see your credentials.</p>
          <div>
            <span className="awsui-util-f-r">
              <Button
                data-testid="signin-button"
                onClick={handleSignIn}
                formAction="none"
              >
                Login
              </Button>
            </span>
          </div>
        </div>
      </ColumnLayout>
    );
  } else if (user) {
    if (
      typeof getUserAttr(user, 'name') == 'string' &&
      getUserAttr(user, 'name').length > 0 &&
      typeof getUserAttr(user, 'email') == 'string' &&
      getUserAttr(user, 'email').length > 0
    ) {
      const name = getUserAttr(user, 'name');
      const email = getUserAttr(user, 'email');
      const allValid = isValidName(name) && isValidEmail(email);
      head = allValid ? (
        <h2>SSO authentication handshake successful</h2>
      ) : (
        <h2>SSO authentication handshake successful</h2>
      );
      body = (
        <ColumnLayout>
          <div data-awsui-column-layout-root="true">
            <p>See attributes received:&nbsp;</p>
            <AttributeRow
              label="Name"
              value={name}
              isValid={isValidName(name)}
              warningMessage="Expected: full name (e.g., Jane Doe)"
              warningVariant="error"
            />
            <AttributeRow
              label="EmailAddress"
              value={email}
              isValid={isValidEmail(email)}
              warningMessage="Expected valid email address"
              warningVariant="error"
            />
            <AttributeRow
              label="Login ID"
              value={getUserAttr(user, 'identities')?.[0]
                ?.userId?.split('|')
                .pop()}
              isValid={isValidNameID(
                getUserAttr(user, 'identities')?.[0]?.userId?.split('|').pop(),
                email
              )}
              warningMessage="We recommend that this value be unique and distinct from the user's email address"
              warningVariant="success"
            />
          </div>
        </ColumnLayout>
      );
    } else {
      head = (
        <h2>
          SSO authentication handshake unsuccessful with missing attributes
        </h2>
      );
      const name = getUserAttr(user, 'name');
      body = (
        <ColumnLayout>
          <div data-awsui-column-layout-root="true">
            <AttributeRow
              label="Name"
              value={name}
              isValid={isValidName(name)}
              warningMessage="Expected: full name (e.g., Jane Doe)"
              warningVariant="warning"
              fallback="<missing>"
            />
            <AttributeRow
              label="EmailAddress"
              value={getUserAttr(user, 'email')}
              isValid={isValidEmail(getUserAttr(user, 'email'))}
              warningMessage="Expected valid email address"
              warningVariant="warning"
              fallback="<missing>"
            />
            <AttributeRow
              label="Login ID"
              value={getUserAttr(user, 'identities')?.[0]
                ?.userId?.split('|')
                .pop()}
              isValid={isValidNameID(
                getUserAttr(user, 'identities')?.[0]?.userId?.split('|').pop(),
                getUserAttr(user, 'email')
              )}
              warningMessage="We recommend that this value be unique and distinct from the user's email address"
              warningVariant="success"
              fallback="<missing>"
            />
          </div>
        </ColumnLayout>
      );
    }
  }
  return (
    <div className="App">
      <Header user={user} onLogout={handleSignOut} />
      <Main>
        <Container>
          <FormSection header={<h1 title="heading"> {head}</h1>}>
            {body}
          </FormSection>
        </Container>
      </Main>
      <Footer />
    </div>
  );
};

export default App;
